import { useState } from 'react';
import {
  getTransactionTimeline,
  getProcessDate,
  getBuildingDetailsById,
} from '../../../../auth/_requests';
import { TransactionTimeline } from '../../../utils/types';
import { formatDateToDisplayShortMonth } from '../../../utils/utils';
import { TransactionData } from '../utils/transactionUtils';

interface UseTransactionTimelineProps {
  authToken: string | undefined;
  selectedTransaction: TransactionData | null;
}

interface UseTransactionTimelineReturn {
  timeline: TransactionTimeline | undefined;
  isLoading: boolean;
  fetchTimeline: (transactionId: string | null) => Promise<void>;
}

export const useTransactionTimeline = ({
  authToken,
  selectedTransaction,
}: UseTransactionTimelineProps): UseTransactionTimelineReturn => {
  const [timeline, setTimeline] = useState<TransactionTimeline | undefined>();
  const [processDate, setProcessDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProcessDate = async (date: string): Promise<string | null> => {
    try {
      if (!selectedTransaction || !authToken) {
        console.error('No selected transaction or auth token available');
        return null;
      }

      if (!selectedTransaction.tenantPaymentOptionId) {
        console.error('No tenantPaymentOptionId available for transaction');
        return null;
      }

      const paymentType = selectedTransaction.paymentType || 'direct';

      let dueDate = 1;
      if (selectedTransaction.tenantBuildingId) {
        try {
          const buildingDetails = await getBuildingDetailsById(
            authToken,
            selectedTransaction.tenantBuildingId
          );
          if (buildingDetails?.data?.[0]?.building?.dueDate) {
            dueDate = buildingDetails.data[0].building.dueDate;
          }
        } catch (error) {
          console.error('Error fetching building details for dueDate', error);
        }
      }

      const response = await getProcessDate(
        authToken,
        date,
        dueDate,
        paymentType,
        selectedTransaction.tenantPaymentOptionId
      );
      return formatDateToDisplayShortMonth(response.data.processDate);
    } catch (error) {
      console.error('Error fetching process date', error);
      return null;
    }
  };

  const fetchTimeline = async (transactionId: string | null) => {
    setProcessDate(null);
    if (!transactionId || !authToken) return;

    setIsLoading(true);
    try {
      const response = await getTransactionTimeline(authToken, transactionId);
      const timelineData = response.data;
      setTimeline(timelineData);

      if (timelineData?.paymentSuccess) {
        const result = await fetchProcessDate(timelineData.paymentSuccess);
        setProcessDate(result);
       
      } else {
        setProcessDate(null);
      }
    } catch (error) {
      console.error('Error fetching timeline', error, processDate);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    timeline,
    isLoading,
    fetchTimeline,
  };
};

